import React, { useMemo } from "react";
import { ArticleReference } from "models/AiResearch";
import styles from "./style.scss";
import PdfViewer from "components/PDFViewer";
import { useReduxState } from "hooks/useReduxState";
import { formatDate } from "utils/helper/day";
import { AiResearchText } from "components/AiResearch/AiResearchText";

interface Props {
  reference: ArticleReference;
}

type TextRange = { start: number; end: number };

type TextNodeInfo = {
  node: Node;
  text: string;
  start: number;
  end: number;
};

const normalizeText = (text: string) =>
  text.replace(/\u00a0/g, " ").replace(/\r?\n/g, "");

const toFilteredText = (text: string, shouldDrop: (ch: string) => boolean) => {
  let filtered = "";
  const map: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (shouldDrop(ch)) continue;
    map.push(i);
    filtered += ch;
  }
  return { filtered, map };
};

// テキストノードを平坦化し、ノード境界情報を保持する
const collectTextNodes = (root: HTMLElement) => {
  const nodes: TextNodeInfo[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode() as Text | null;
  let index = 0;

  const isIgnoredText = (node: Node) => {
    const parent = node.parentNode;
    return parent instanceof Element
      ? !!parent.closest("aside, .article-image__explanation")
      : false;
  };

  while (current) {
    const value = current.data ?? "";
    if (!isIgnoredText(current) && value.length > 0) {
      nodes.push({
        node: current,
        text: value,
        start: index,
        end: index + value.length
      });
      index += value.length;
    }
    current = walker.nextNode() as Text | null;
  }

  return { nodes, text: nodes.map(item => item.text).join("") };
};

// テキストノードを跨いで対象範囲を <span> で包む
const applyRangeToTextNodes = (
  nodes: TextNodeInfo[],
  range: TextRange,
  highlightClass: string
) => {
  for (const item of nodes) {
    const { node, text, start, end } = item;
    if (end <= range.start || start >= range.end) continue;

    const localStart = Math.max(range.start, start) - start;
    const localEnd = Math.min(range.end, end) - start;
    if (localStart >= localEnd) continue;

    const fragment = document.createDocumentFragment() as Node;
    if (localStart > 0) {
      fragment.appendChild(document.createTextNode(text.slice(0, localStart)));
    }
    const span = (document.createElement("span") as unknown) as Node & {
      className: string;
    };
    span.className = highlightClass;
    span.textContent = text.slice(localStart, localEnd);
    fragment.appendChild(span);
    if (localEnd < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(localEnd)));
    }

    const parent = node.parentNode;
    parent?.replaceChild(fragment, node);
  }
};

const buildMatchRange = (
  normalizedBody: string,
  normalizedSource: string,
  dropFn: (ch: string) => boolean,
  extendPeriod = false
): TextRange | null => {
  if (!normalizedBody || !normalizedSource) return null;

  const bodyFiltered = toFilteredText(normalizedBody, dropFn);
  const sourceFiltered = toFilteredText(normalizedSource, dropFn);
  if (!sourceFiltered.filtered) return null;

  const idx = bodyFiltered.filtered.indexOf(sourceFiltered.filtered);
  if (idx === -1) return null;

  const start = bodyFiltered.map[idx];
  let end = bodyFiltered.map[idx + sourceFiltered.filtered.length - 1] + 1;
  if (start === undefined || end === undefined) return null;

  if (extendPeriod && !normalizedSource.includes("。")) {
    while (end < normalizedBody.length && normalizedBody[end] === "。") {
      end += 1;
    }
  }

  return { start, end };
};

const buildPrefixMatchRange = (
  normalizedBody: string,
  normalizedSource: string,
  dropFn: (ch: string) => boolean,
  minLen = 8
): TextRange | null => {
  const bodyFiltered = toFilteredText(normalizedBody, dropFn);
  const sourceFiltered = toFilteredText(normalizedSource, dropFn);

  for (let len = sourceFiltered.filtered.length; len >= minLen; len -= 1) {
    const prefix = sourceFiltered.filtered.slice(0, len);
    const idx = bodyFiltered.filtered.indexOf(prefix);
    if (idx === -1) continue;

    const start = bodyFiltered.map[idx];
    const end = bodyFiltered.map[idx + len - 1] + 1;
    if (start !== undefined && end !== undefined) return { start, end };
  }

  return null;
};

const findMatchRange = (bodyText: string, sourceText: string) => {
  if (!bodyText || !sourceText) return null;

  const normalizedBody = normalizeText(bodyText);
  const normalizedSource = normalizeText(sourceText);
  if (!normalizedSource) return null;

  const dropSpaces = (ch: string) => /\s/.test(ch);
  const dropSpacesAndPeriod = (ch: string) => /\s/.test(ch) || ch === "。";

  return (
    buildMatchRange(normalizedBody, normalizedSource, dropSpaces) ??
    buildMatchRange(
      normalizedBody,
      normalizedSource,
      dropSpacesAndPeriod,
      true
    ) ??
    buildPrefixMatchRange(normalizedBody, normalizedSource, dropSpaces)
  );
};

// 本文 HTML の一致箇所を span でハイライトする
const addHighlightToHtml = (
  bodyHtml: string,
  sourceText: string,
  highlightClass: string,
  tableClass?: string
) => {
  if (!bodyHtml) return bodyHtml;
  if (typeof document === "undefined") return bodyHtml;

  const container = document.createElement("div");
  container.innerHTML = bodyHtml;
  if (tableClass) {
    container
      .querySelectorAll("table")
      .forEach(table => table.classList.add(tableClass));
  }

  if (!sourceText) return container.innerHTML;

  const { nodes, text: bodyText } = collectTextNodes(container);
  if (!bodyText) return bodyHtml;

  const range = findMatchRange(bodyText, sourceText);
  if (!range) return container.innerHTML;

  applyRangeToTextNodes(nodes, range, highlightClass);
  return container.innerHTML;
};

export const Article: React.FC<Props> = ({ reference }) => {
  const { trialFlg } = useReduxState(state => state.auth.saved.user);
  const sourceText = reference.data.source?.replace(/\r?\n/g, "") ?? "";

  const highlightedBody = useMemo(() => {
    if (!reference.body) return reference.body;
    return addHighlightToHtml(
      reference.body,
      sourceText,
      styles.highlight,
      styles.vsTable
    );
  }, [reference.body, sourceText]);

  return (
    <div className={styles.article}>
      <span className={styles.citationNumber}>{reference.citationNumber}</span>
      <h2 className={styles.articleTitle}>{reference.data.title}</h2>
      <div className={styles.meta}>
        <span>
          {formatDate(String(reference.data.publishDate), "YYYY年MM月DD日")}
        </span>
        <span>{reference.data.mediaName}</span>
      </div>
      {reference.sourcePdf ? (
        <div className={styles.pdfViewer}>
          <PdfViewer
            src={reference.sourcePdf}
            minHeight={500}
            visibleSaveButton={!trialFlg} // トライアルユーザーの場合は保存ボタンを非表示
            visiblePrintButton={!trialFlg} // トライアルユーザーの場合は印刷ボタンを非表示
            highlightText={sourceText} // ハイライトするテキスト
          />
        </div>
      ) : reference.body ? (
        <div
          className={styles.articleContent}
          dangerouslySetInnerHTML={{ __html: highlightedBody ?? "" }}
        />
      ) : reference.data.source ? (
        <AiResearchText
          text={reference.data.source}
          onClickReference={() => {}}
        />
      ) : null}
    </div>
  );
};
